export type AssuranceLevel = 'High' | 'Substantial' | 'Moderate' | 'Limited' | 'None' | 'NaN';
/** An Assurance Level a metric actually reached; ``NaN`` is the absence of one. */
export type MeasuredAssuranceLevel = Exclude<AssuranceLevel, 'NaN'>;
export type ComplianceStop = 1 | 2 | 3 | 4 | 5 | 'na';

export const ASSURANCE_TO_COMPLIANCE: Record<AssuranceLevel, ComplianceStop> = {
  None: 1,
  Limited: 2,
  Moderate: 3,
  Substantial: 4,
  High: 5,
  NaN: 'na'
};

/**
 * Classic dashboard palette (High = green). Used by the dial-layout tiles
 * when `dashboard_classic_score_cards` is on.
 */
export const CLASSIC_ASSURANCE_COLORS: Record<AssuranceLevel, string> = {
  High: '#166534',
  Substantial: '#66BB6A',
  Moderate: '#FFEB3B',
  Limited: '#FF9800',
  None: '#F44336',
  NaN: '#64748b'
};

/**
 * The platform's modified Okabe–Ito hues for categorical chart series, without
 * black (it reads as text). Sky and vermillion are CompliMind's alterations.
 * Keep in sync with `okabe` in tailwind.config.js (sky and vermillion with
 * `okabe.complimind`).
 */
export const OKABE_ITO = {
  blue: '#0072B2',
  orange: '#E69F00',
  green: '#009E73',
  purple: '#CC79A7',
  sky: '#78CAE5',
  vermillion: '#D64D07',
  yellow: '#F0E442'
} as const;

/** Saturated hex for SVG/canvas. Keep in sync with `compliance` / `oi` in tailwind.config.js. */
export const COMPLIANCE_COLORS: Record<ComplianceStop, string> = {
  1: '#D64D07',
  2: '#E69F00',
  3: '#F0E442',
  4: '#78CAE5',
  5: '#0072B2',
  na: '#CBD5E1'
};

export const ASSURANCE_COLORS: Record<AssuranceLevel, string> = {
  None: COMPLIANCE_COLORS[1],
  Limited: COMPLIANCE_COLORS[2],
  Moderate: COMPLIANCE_COLORS[3],
  Substantial: COMPLIANCE_COLORS[4],
  High: COMPLIANCE_COLORS[5],
  NaN: COMPLIANCE_COLORS.na
};

/** Lightest family tint as fill; darker family stop as label. */
const COMPLIANCE_SURFACE: Record<ComplianceStop, string> = {
  1: 'bg-oi-vermillion-100 text-oi-vermillion-900',
  2: 'bg-oi-orange-100 text-oi-orange-900',
  3: 'bg-oi-yellow-100 text-oi-yellow-900',
  4: 'bg-oi-sky-100 text-oi-sky-900',
  5: 'bg-oi-blue-100 text-oi-blue-900',
  na: 'bg-bluegray-200 text-content-default'
};

export function complianceClass(stop: ComplianceStop): string {
  return COMPLIANCE_SURFACE[stop];
}

export function complianceBg(level: AssuranceLevel): string {
  return complianceClass(ASSURANCE_TO_COMPLIANCE[level]);
}

/** Pick black/white text for contrast against an assurance colour swatch. */
export function getTextColorForBg(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const sr = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const sg = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const sb = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  const luminance = 0.2126 * sr + 0.7152 * sg + 0.0722 * sb;
  return luminance > 0.5 ? '#111827' : '#ffffff';
}

/** Hex palette for dashboard tiles: OI blue=high by default, classic green=high when classic tiles are on. */
export function dashboardAssuranceColors(useChartTiles: boolean): Record<AssuranceLevel, string> {
  return useChartTiles ? ASSURANCE_COLORS : CLASSIC_ASSURANCE_COLORS;
}
